import {
    pendingTask,
    begin,
    end,
} from 'react-redux-spinner';
import { toastr } from 'react-redux-toastr';
import { push } from 'redux-first-history';
import { cloneDeep } from 'lodash';
import {
    convertNullableDateTimeToUkgUserDateTime,
    // eslint-disable-next-line import/no-unresolved
} from '@commonClient2/services/ukgDateTime';
import { LOADING_START, LOADING_END } from '../../../store/loading';
import { fetchEmployeeAppData } from '../appData/appData.middlewares';
import { UPDATE_EMPLOYEE_BIDDABLE_PROFILE, UPDATE_EMPLOYEE_BIDDED_PROFILE } from './bidProfile.actions';
import * as api from '../../services/api-endpoints';
import { EmployeeBidProfileWarningType } from '../../../Enums';
import {
    setInitSelectionValues, updateSelection, RESET_BID_SELECTION, updateShowWeeklyHours,
} from '../../../store/bidSelection';
import { parseEmployeeBidProfileServerModel, bidSelectionToMap } from '../../services/utilities/employeeBidProfileModelMapper';
import { simplifySequentialSchedule } from '../../../Manager/services/BidProfileModelMapper';
import i18n from '../../../services/i18n';

/**
 * @param {string} profileId
 */
// eslint-disable-next-line import/prefer-default-export
export const selectedBiddableProfile = (profileId) => /** @param {import('redux').Dispatch} dispatch */ async (dispatch) => {
    dispatch({ type: LOADING_START, [pendingTask]: begin });
    try {
        const result = await api.getBidProfile(profileId);
        if (result.status === 200) {
            const bidProfile = result.data;
            // @ts-ignore
            dispatch(setInitSelectionValues(
                bidProfile.items,
                bidProfile.employeeBidSelection?.selection || {},
                bidProfile.anchorDay,
                bidProfile.scheduleSource,
                bidProfile.shifts,
                bidProfile.paycodes,
                bidProfile.dayCount,
                bidProfile.periodType,
                null,
                bidProfile.isGroupShiftlinesWithSameSchedules,
                bidProfile.employeeBidSelection?.isDraft || false,
            ));

            const parsedBidProfile = parseEmployeeBidProfileServerModel(result);
            // Show weekly hours by default, if required
            updateShowWeeklyHours(parsedBidProfile.weeklyHoursByDefault, dispatch);
            
            // @ts-ignore
            dispatch({
                type: UPDATE_EMPLOYEE_BIDDABLE_PROFILE,
                payload: parsedBidProfile,
            });
            dispatch(push('/Employee/bid'));
        } else {
            toastr.error(i18n.t('shiftBidding:toastr.fetchingBidInformation'), i18n.t('shiftBidding:toastr.failedToFetch'));
        }
    } catch (error) {
        toastr.error(i18n.t('shiftBidding:toastr.fetchingBidInformation'), error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

/**
 * @param {string} profileId
 * @param {boolean?} isRedirect
 */
export const selectedBiddedProfile = (profileId, isRedirect = true) =>
/** @param {import('redux').Dispatch} dispatch */ async (dispatch) => {
        try {
            dispatch({ type: LOADING_START, [pendingTask]: begin });
            const result = await api.getBidProfileSummary(profileId);
            if (result.status === 200) {
                const bidProfileSummary = result.data;
                // @ts-ignore
                dispatch(setInitSelectionValues(
                    bidProfileSummary.items || [],
                    bidProfileSummary.employeeBidSelection?.selection || {},
                    bidProfileSummary.anchorDay,
                    bidProfileSummary.scheduleSource,
                    bidProfileSummary.shifts,
                    bidProfileSummary.paycodes,
                    bidProfileSummary.dayCount,
                    bidProfileSummary.periodType,
                    bidProfileSummary.approvedItemGroupId,
                    bidProfileSummary.isGroupShiftlinesWithSameSchedules,
                    bidProfileSummary.employeeBidSelection?.isDraft || false,
                ));

                const selection = bidProfileSummary.employeeBidSelection?.selection || [];
                const items = Object.keys(selection).map((key) => {
                    const item = bidProfileSummary.items.find((x) => x.groupId === selection[key]);
                    return {
                        ...item,
                        tableOrder: key,
                    };
                });

                // Show weekly hours by default, if required
                updateShowWeeklyHours(bidProfileSummary.weeklyHoursByDefault, dispatch);

                dispatch({
                    type: UPDATE_EMPLOYEE_BIDDED_PROFILE,
                    payload: {
                        ...bidProfileSummary,
                        items,
                        originalItems: cloneDeep(items),
                        creationDate: convertNullableDateTimeToUkgUserDateTime(bidProfileSummary.creationDate),
                        lastUpdate: convertNullableDateTimeToUkgUserDateTime(bidProfileSummary.lastUpdate),
                        employeeSubmissionDate: convertNullableDateTimeToUkgUserDateTime(bidProfileSummary.employeeSubmissionDate),
                        submissionPeriodStartDate: convertNullableDateTimeToUkgUserDateTime(bidProfileSummary.submissionPeriodStartDate),
                        submissionPeriodEndDate: convertNullableDateTimeToUkgUserDateTime(bidProfileSummary.submissionPeriodEndDate),
                        requestPeriodStartDate: convertNullableDateTimeToUkgUserDateTime(bidProfileSummary.requestPeriodStartDate),
                        requestPeriodEndDate: convertNullableDateTimeToUkgUserDateTime(bidProfileSummary.requestPeriodEndDate),
                        sequentialSchedule: simplifySequentialSchedule(bidProfileSummary.sequentialSchedule),
                    }
                });

                if (isRedirect) {
                    dispatch(push('/Employee/summary'));
                }
            } else {
                toastr.error(i18n.t('shiftBidding:toastr.fetchingBidSummaryInformation'), i18n.t('shiftBidding:toastr.failedToFetchBidSummaryInformation'));
            }
        } catch (error) {
            toastr.error(i18n.t('shiftBidding:toastr.fetchingBidSummaryInformation'), error.response.data.Message);
        } finally {
            dispatch({ type: LOADING_END, [pendingTask]: end });
        }
    };

/**
 * @param {string} profileId
 * @param {string[]} employeeSelections
 */
// eslint-disable-next-line max-len
export const saveBidSelectionDraft = (profileId, employeeSelections) => /** @param {import('redux').Dispatch} dispatch */ async (dispatch) => {
    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        if (!employeeSelections.length) {
            toastr.error(i18n.t('shiftBidding:toastr.savingSelectionDraft'), i18n.t('shiftBidding:toastr.pleaseChooseAtLeastOneSelection'));
            return;
        }
        const response = await api.saveBidSelectionDraft(profileId, { selections: bidSelectionToMap(employeeSelections) });
        if (response.status === 200) {
            const bidProfile = parseEmployeeBidProfileServerModel(response);

            dispatch({
                type: UPDATE_EMPLOYEE_BIDDABLE_PROFILE,
                payload: bidProfile,
            });
            toastr.success(i18n.t('shiftBidding:toastr.bidDraftSubmittedSuccessfully'), '');
        } else {
            toastr.error(i18n.t('shiftBidding:toastr.savingSelectionDraft'), i18n.t('shiftBidding:toastr.failedToSave'));
        }
    } catch (error) {
        toastr.error(i18n.t('shiftBidding:toastr.savingSelectionDraft'), error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

export const autoSaveBidSelectionDraft = () => /** @param {import('redux').Dispatch} dispatch */ async (dispatch, getState) => {
    try {
        const { employeeBidSelection, employeeBidProfile } = getState();
        const { employeeSelections } = employeeBidSelection;
        const { profileId } = employeeBidProfile;
        if (!employeeBidSelection.isDraft || !employeeSelections.length) {
            return;
        }
        await api.saveBidSelectionDraft(profileId, { selections: bidSelectionToMap(employeeSelections) });
    } catch (error) {
        console.warn('Auto-save failed:', error.response?.data?.Message || error.message);
    }
};

/**
 * @param {string} profileId
 * @param {string[]} employeeSelections
 */
// eslint-disable-next-line max-len
export const saveBidSelection = (profileId, employeeSelections) => /** @param {import('redux').Dispatch} dispatch */ async (dispatch) => {
    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        if (!employeeSelections.length) {
            toastr.error(i18n.t('shiftBidding:toastr.savingSelection'), i18n.t('shiftBidding:toastr.pleaseChooseAtLeastOneSelection'));
            return;
        }
        const response = await api.saveBidSelection(profileId, { selections: bidSelectionToMap(employeeSelections) });
        if (response.status === 200) {
            const bidProfile = parseEmployeeBidProfileServerModel(response);

            dispatch({
                type: UPDATE_EMPLOYEE_BIDDABLE_PROFILE,
                payload: bidProfile,
            });
            toastr.success(i18n.t('shiftBidding:toastr.bidSubmittedSuccessfully'), '');
            dispatch({ type: RESET_BID_SELECTION });
            dispatch(push('/Employee'));
        } else {
            toastr.error(i18n.t('shiftBidding:toastr.submittingBid'), i18n.t('shiftBidding:toastr.failedToSave'));
        }
    } catch (error) {
        toastr.error(i18n.t('shiftBidding:toastr.submittingBid'), error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

/**
 * @param {string} profileId
 */
// eslint-disable-next-line max-len
export const declineBidSelection = (profileId) => /** @param {import('redux').Dispatch} dispatch */ async (dispatch) => {
    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });

        const response = await api.declineBidSelection(profileId);
        if (response.status === 200) {
            const bidProfile = parseEmployeeBidProfileServerModel(response);

            dispatch({
                type: UPDATE_EMPLOYEE_BIDDABLE_PROFILE,
                payload: bidProfile,
            });
            toastr.success(i18n.t('shiftBidding:toastr.decliningEmployeeBidSelection'), i18n.t('shiftBidding:toastr.bidEmployeesDeclinedSuccessfully'));
            dispatch({ type: RESET_BID_SELECTION });
            dispatch(push('/Employee'));
        } else {
            toastr.error(i18n.t('shiftBidding:toastr.decliningEmployeeBidSelection'), i18n.t('shiftBidding:toastr.failedToDecline'));
        }
    } catch (error) {
        toastr.error(i18n.t('shiftBidding:toastr.decliningEmployeeBidSelection'), error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

/**
 * @param {string} profileId
 * @param {string[]} employeeSelections
 */
// eslint-disable-next-line max-len
export const saveBidSelectionTemplate = (profileId, employeeSelections) => /** @param {import('redux').Dispatch} dispatch */ async (dispatch) => {
    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        if (!employeeSelections.length) {
            toastr.error(i18n.t('shiftBidding:toastr.savingTemplate'), i18n.t('shiftBidding:toastr.pleaseChooseAtLeastOneSelection'));
            return;
        }
        const response = await api.saveBidSelectionTemplate(profileId, { selections: bidSelectionToMap(employeeSelections) });
        if (response.status === 200) {
            toastr.success(i18n.t('shiftBidding:toastr.templateSavedSuccessfully'), '');
            const result = await api.getBidProfile(profileId);
            const parsedBidProfile = parseEmployeeBidProfileServerModel(result);
            dispatch({
                type: UPDATE_EMPLOYEE_BIDDABLE_PROFILE,
                payload: parsedBidProfile,
            });
        } else {
            toastr.error(i18n.t('shiftBidding:toastr.savingTemplate'), i18n.t('shiftBidding:toastr.failedToSave'));
        }
    } catch (error) {
        toastr.error(i18n.t('shiftBidding:toastr.savingTemplate'), error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

/**
 * @param {string} profileId
 * @param {EmployeeBidProfile.IGroupedScheduleTemplateItems[]} items
 * @param {boolean} isGroupShiftlinesWithSameSchedules
 */
// eslint-disable-next-line max-len
export const loadBidSelectionTemplate = (profileId, items, isGroupShiftlinesWithSameSchedules) => /** @param {import('redux').Dispatch} dispatch */ async (dispatch) => {
    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        const response = await api.loadBidSelectionTemplate(profileId);
        if (response.status === 200) {
            if (response.data === null) {
                toastr.error(i18n.t('shiftBidding:toastr.loadingTemplate'), i18n.t('shiftBidding:toastr.youDoNotHaveSavedTemplate'));
                return;
            }
            const preferentialSelection = response.data.selectionDetails?.preferentialSelection || [];
            const selectedKeys = Object.values(preferentialSelection);

            const addedPatternsWarnings = response.data.warnings.schedulePatternTemplateNamesAdded.map((x) => ({
                type: EmployeeBidProfileWarningType.SchedulePatternAdded,
                details: x,
            }));
            const removedPatternsWarnings = response.data.warnings.schedulePatternTemplateNamesRemoved.map((x) => ({
                type: EmployeeBidProfileWarningType.SchedulePatternRemoved,
                details: x,
            }));

            dispatch(updateSelection(items, selectedKeys, isGroupShiftlinesWithSameSchedules));
            dispatch({
                type: UPDATE_EMPLOYEE_BIDDABLE_PROFILE,
                payload: {
                    warnings: addedPatternsWarnings.concat(removedPatternsWarnings),
                },
            });
            toastr.success(i18n.t('shiftBidding:toastr.templateLoadedSuccessfully'), '');
        } else {
            toastr.error(i18n.t('shiftBidding:toastr.loadingTemplate'), i18n.t('shiftBidding:toastr.failedToLoad'));
        }
    } catch (error) {
        toastr.error(i18n.t('shiftBidding:toastr.loadingTemplate'), error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

/**
 * @param {string} profileId
 * @param {boolean} isUpdateSummaryPage
 */
export const lockAwardedShiftline = (profileId, isUpdateSummaryPage) =>
    /** @param {import('redux').Dispatch} dispatch */ async (dispatch) => {
        try {
            dispatch({ type: LOADING_START, [pendingTask]: begin });
            const response = await api.lockAwardedShiftline(profileId);
            if (response.status === 200) {
                dispatch(fetchEmployeeAppData());
                if (isUpdateSummaryPage) {
                    dispatch({
                        type: UPDATE_EMPLOYEE_BIDDED_PROFILE,
                        payload: {
                            approvedShiftlineInfos: response.data.approvedShiftlineInfos,
                        },
                    });
                }
                toastr.success(i18n.t('shiftBidding:toastr.awardedShiftlineLockedSuccessfully'), '');
            } else {
                toastr.error(i18n.t('shiftBidding:toastr.lockingAwardedShiftline'), i18n.t('shiftBidding:toastr.failedToLock'));
            }
        } catch (error) {
            toastr.error(i18n.t('shiftBidding:toastr.lockingAwardedShiftline'), error.response.data.Message);
        } finally {
            dispatch({ type: LOADING_END, [pendingTask]: end });
        }
    };

/**
 * @param {string} profileId
 * @param {boolean} isUpdateSummaryPage
 */
export const unlockAwardedShiftline = (profileId, isUpdateSummaryPage) =>
    /** @param {import('redux').Dispatch} dispatch */ async (dispatch) => {
        try {
            dispatch({ type: LOADING_START, [pendingTask]: begin });
            const response = await api.unlockAwardedShiftline(profileId);
            if (response.status === 200) {
                dispatch(fetchEmployeeAppData());
                if (isUpdateSummaryPage) {
                    dispatch({
                        type: UPDATE_EMPLOYEE_BIDDED_PROFILE,
                        payload: {
                            approvedShiftlineInfos: response.data.approvedShiftlineInfos,
                        },
                    });
                }
                toastr.success(i18n.t('shiftBidding:toastr.awardedShiftlineUnlockedSuccessfully'), '');
            } else {
                toastr.error(i18n.t('shiftBidding:toastr.unlockingAwardedShiftline'), i18n.t('shiftBidding:toastr.failedToLock'));
            }
        } catch (error) {
            toastr.error(i18n.t('shiftBidding:toastr.unlockingAwardedShiftline'), error.response.data.Message);
        } finally {
            dispatch({ type: LOADING_END, [pendingTask]: end });
        }
    };
